import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ConfigStructure} from '../../config/config-structure.model';
import {Property} from '../property/property.model';
import {TranslatorService} from '../translator.service';
import {DescriptorEnum} from '../../config/descriptors/descriptor.enum';
import {GroupedConfigStructure} from '../../config/grouped-config-structure.model';
import {DescriptorLink} from "../property/descriptor-link.model";
import {FilterModalComponent} from '../../search/search-list/filters/filter-block/filter-modal/filter-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DescriptorMetadataModalComponent} from './descriptor-metadata-modal-component/descriptor-metadata-modal-component';

@Component({
  selector: 'app-descriptor-metadata',
  templateUrl: './descriptor-metadata.component.html',
  styleUrls: ['./descriptor-metadata.component.css']
})
export class DescriptorMetadataComponent implements OnInit, OnChanges {

  static MOBILE_DETAILS_LIMIT = 2;

  @Input() descType: DescriptorEnum;

  @Input() onlyOneColumn: boolean = false;

  @Input() disableShowMore: false;

  @Input() statsLibTxt;

  @Input() hideSearchListElements = true;

  @Input() wikiData;

  @Input() wikiDataId;

  @Input() strippedVersion: boolean;

  @Input() objectVersion: boolean;

  @Input() showMulti: boolean;

  @Input() set properties(value: Property[]) {
    if (value) {
      this._properties = value;
      this.propertiesReady = true;
    }
  }

  @Input() set config(value: ConfigStructure[]) {
    if (value) {
      this._config = value;
      this.configReady = true;
    }
  }

  allMobileDetails = false;

  evenGroupedConfigs: GroupedConfigStructure[] = [];

  oddGroupedConfigs: GroupedConfigStructure[] = [];

  geographicalDesc = false;

  characterLimit: number = 20;

  private propertiesReady = false;

  private configReady = false;

  _properties: Property[] = [];

  private _config: ConfigStructure[] = [];

  constructor(private translator: TranslatorService, protected modalService: NgbModal) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.propertiesReady && this.configReady) {
      this.updateElements();
    }
  }

  ngOnInit() {
    this.geographicalDesc = (DescriptorEnum.GEOGRAPHICAL === this.descType);
  }

  get properties(): Property[] {
    return this._properties;
  }

  getPropertyValue(config: ConfigStructure, returnRaw: boolean): any {
    const result = this.translator.translate(config, this._properties, returnRaw);
    if (result && returnRaw) return result;
    if (result) {
      return result.trim() + " ";
    } else {
      return '';
    }
  }

  getPropertyValueCutted(config: ConfigStructure): string {
    return this.getPropertyValue(config, false).slice(0, this.characterLimit);
  }

  openModal(type){
    let modalInstance = this.modalService.open(DescriptorMetadataModalComponent);
    modalInstance.componentInstance.data = this.wikiData;
    modalInstance.componentInstance.type = type;
    modalInstance.componentInstance.properties = this._properties;
    modalInstance.componentInstance.config = this._config;
  }

  getProperty(config: ConfigStructure) {
    let property = this.translator.find(this._properties, config.template);
    if (config.nestedConfig.length > 0) {
      if (property && Array.isArray(property.value)) {
        property.value.forEach((prop) => {
          property = this.translator.find(prop, config.nestedConfig[0].template);
        });
      } else if (property) {
        property = this.translator.find(<Property[]>property.value, config.nestedConfig[0].name);
      }
    }
    return property;
  }

  getLinks(configs: ConfigStructure[]): DescriptorLink[] {
    let links = [];
    configs.forEach(config =>{
      let prop = this.getProperty(config);
      if (prop && prop.value){
        links.push(...prop.value);
      }
    });
    return links;
  }

  hasValue(config: ConfigStructure): boolean {
    let propertyValue = this.getPropertyValue(config, false);
    let property = this.getProperty(config);
    return !!((!config.isLink && propertyValue.trim())
      || (config.isLink && config.nestedConfig.length === 0 && property && property.value)
      || (config.isLink && (!property || !property.value) && config.allMatches && config.elseConfig && this.getPropertyValue(config.elseConfig, false))
      || (config.isLink && this.hasNotEmptyPropertyInNested(config))
    );
  }

  hasEnoughDetails() {
    return this.evenGroupedConfigs.length+this.oddGroupedConfigs.length > DescriptorMetadataComponent.MOBILE_DETAILS_LIMIT;
  }

  toggleMobileDetails() {
    this.allMobileDetails = !this.allMobileDetails;
  }

  getMobileDetailsLabel():string {
    if (this.allMobileDetails){
      return 'object-details.show-less';
    }else{
      return 'object-details.show-more';
    }
  }

  private hasNotEmptyPropertyInNested(config: ConfigStructure): boolean {
    let ret = false;
    if (config.nestedConfig) {
      config.nestedConfig.forEach(value => {
        let property = this.getProperty(value);
        if (property && property.value) {
          ret = true;
          return;
        }
      });
    }
    return ret;
  }

  private updateElements() {
    const groupedConfigs = [];
    let lastRowWithColumns: GroupedConfigStructure;
    this._config
      .filter(element => this.hasValue(element))
      .forEach(element => {
      const row = !element.skipNewLine;
      if (row) {
        lastRowWithColumns = null;
        groupedConfigs.push(new GroupedConfigStructure(element));
      } else {
        if (lastRowWithColumns) {
          lastRowWithColumns.append(element);
        } else {
          lastRowWithColumns = new GroupedConfigStructure(element);
          groupedConfigs.push(lastRowWithColumns);
        }
      }
    });
    this.evenGroupedConfigs = [];
    this.oddGroupedConfigs = [];
    groupedConfigs.forEach((value, index) => {
      if (index%2==0){
        this.evenGroupedConfigs.push(value);
      }else{
        this.oddGroupedConfigs.push(value);
      }
    });

    /*
    Creates new array instance to preserve adding items to existing array then marks elements which should be hideable in mobile version.
    Functionality doesn't affect desktop version.
     */
    Array.from(this.evenGroupedConfigs).concat(this.oddGroupedConfigs).forEach((value, index) => {
      value.mobileLimit = index >= DescriptorMetadataComponent.MOBILE_DETAILS_LIMIT;
    });
  }
}
